import React, { useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { DataRoomNodeProps } from '../../types';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import { Checkbox, Collapsible } from 'ncoded-component-library';
import FolderIcon from 'icons/Folder.icon';
import utils, { getDataRoomNodeType, getShowNodeHasBuyerFiles } from 'utils';
import { NODE_COMPONENT } from '../../data';
import { useTranslation } from 'react-i18next';
import FileRowPlaceholder from '../FileRowPlaceholder';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useCallbackRef from 'hooks/useCallbackRef';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';

import './DataRoomFolder.styles.scss';

const DataRoomFolder: React.FC<DataRoomNodeProps> = (props) => {
  const {
    className,
    node,
    disabledLabel,
    buyerLabel,
    onChange,
    updateFolder,
    getChildren,
  } = props;

  const {
    _id,
    name,
    folderSize,
    checked,
    isMixed,
    disabled,
    children,
    childrenIds,
  } = node;

  const hasChildren = !!children?.length;

  const [isLoading, setIsLoading] = useState(!hasChildren);
  const [isDisabledCheckbox, setIsDisabledCheckbox] = useState(false);

  const [collapsible, collapsibleRef] = useCallbackRef<CollapsibleRef>(null);

  const isAlreadyFetched = useRef(false);

  const hasBuyerFiles = getShowNodeHasBuyerFiles(node);

  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const classes = classNames('data-room-folder', { disabled }, className);

  useEffect(() => {
    if (isAlreadyFetched.current || !collapsible?.open || hasChildren) return;

    (async () => {
      try {
        setIsLoading(true);
        setIsDisabledCheckbox(true);

        const { data } = await getChildren(_id);

        updateFolder(node?._id, data);
        isAlreadyFetched.current = true;
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
        setIsDisabledCheckbox(false);
      }
    })();
  }, [
    _id,
    hasChildren,
    node,
    collapsible?.open,
    getChildren,
    updateFolder,
    popServerError,
  ]);

  return (
    <Collapsible
      className={classes}
      ref={collapsibleRef}
      trigger={
        <div
          className={classNames('data-room-folder__trigger', {
            selected: checked,
          })}
        >
          <div className="wrapper">
            <div className="name">
              <ChevrondownIcon
                className={classNames('chevron-icon', {
                  rotated: collapsible?.open,
                })}
              />
              <FolderIcon />
              <p>{name}</p>

              {disabled && <p>{disabledLabel}</p>}
            </div>

            <p>{folderSize ? utils.humanizeFileSize(folderSize) : '-'}</p>

            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={checked}
                disabled={disabled || isDisabledCheckbox}
                className={classNames({ mixed: isMixed })}
                onChange={(checked) => onChange(node?._id, !!checked)}
              />
            </div>
          </div>

          {buyerLabel && hasBuyerFiles && <p>{buyerLabel}</p>}
        </div>
      }
    >
      <ul className="data-room-folder__nodes">
        {isLoading ? (
          Array(childrenIds.length || 3)
            .fill(null)
            .map((_, index) => <FileRowPlaceholder key={index} />)
        ) : children?.length ? (
          children.map((node) => {
            const nodeType = getDataRoomNodeType(node);

            return (
              <li key={node._id}>
                {node &&
                  NODE_COMPONENT[nodeType]({
                    node,
                    buyerLabel,
                    onChange,
                    updateFolder,
                    getChildren,
                  })}
              </li>
            );
          })
        ) : (
          <p className="data-room-folder__empty">{t('emptyFolder')}</p>
        )}
      </ul>
    </Collapsible>
  );
};

export default DataRoomFolder;
